import React, { useState } from 'react';
import styled from 'styled-components';
import { Grid, Type, Breakpoints } from '../../../app/StyleGuide';
import { InlineList } from '../../../components/Layout';
import { SmallToggleRoundedIconButton } from '../../../components/Buttons';
import TrainingPosterPlaceholder from './TrainingPosterPlaceholder';
import { TrainingPoster } from '../../../components/TrainingElements';
import PathwayTrack, { TrackContainer, TrackLine } from './PathwayTrack';
import CourseSteps from './CourseSteps';
import TrainingOverview from './TrainingOverview';
import capitalizeFilter from '../../../filters/capitalize-filter';
import WhiteLabel from '../../../filters/WhiteLabel';
import trainingService from '../../../services/trainingService';
import { CourseResponse } from '../models/courseResponse';
import { NextAction } from '../models/nextAction';
import { UserTrainingAccess } from '../models/UserTrainingAccess';

const CourseContainer = styled.div`
  margin-bottom: ${Grid._5};
  &:last-child {
    margin-bottom: 0;
  }
  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    margin-bottom: ${Grid._7};
  }
`;

const CourseRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const CourseRowItem = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: ${Grid._4};
`;

const CourseInfoContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${Grid._4};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    flex-direction: row;
  }
`;

const CoursePosterContainer = styled.div`
  width: 100%;

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    width: 200px;
  }
`;

const CollapsibleContent = styled.div<{ isOpen: boolean }>`
  display: grid;
  grid-template-rows: ${props => (props.isOpen ? '1fr' : '0fr')};
  transition: grid-template-rows 0.3s ease-in-out;
  margin-top: 24px;
  overflow: hidden;
  position: relative;
`;

const CollapsibleInner = styled.div`
  min-height: 0;

  > div:first-child {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: ${Grid._5};
  }

  > div:last-child {
    margin-left: 36px;
  }
`;

const TrainingInformation = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._3};
  height: 100%;
  h2 {
    color: ${Type.Color.dark};
    font-size: ${Type.Scale._4};
    font-weight: ${Type.Weight.semibold};
    line-height: ${Type.Leading.normal};
    margin: 0;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PathwayCourse = ({
  course,
  trainingId,
  ownerName,
  index,
  length,
  nextAction,
  userAccess,
}: {
  course: CourseResponse;
  trainingId: string;
  ownerName: string;
  index: number;
  length: number;
  nextAction?: NextAction;
  userAccess: UserTrainingAccess;
}) => {
  const [showSteps, setShowSteps] = useState(false);

  const toggleShowSteps = () => setShowSteps(prevState => (prevState = !prevState));

  return (
    <CourseContainer>
      <CourseRow>
        <CourseRowItem>
          <div>
            <PathwayTrack index={index + 1} length={length} />
          </div>
          <CourseInfoContainer>
            <CoursePosterContainer>
              {course.posterImage ? (
                <TrainingPoster src={trainingService.getTrainingImageUrl(course.posterImage)} alt={course.name} />
              ) : (
                <TrainingPosterPlaceholder ownerName={ownerName} />
              )}
            </CoursePosterContainer>
            <div style={{ flex: 1 }}>
              <TrainingInformation>
                <h2 onClick={toggleShowSteps}>{course.name}</h2>
                <InlineList content="•" fontSize={Type.Scale._2}>
                  <li>
                    {course.tasks.length} {capitalizeFilter(WhiteLabel.labels.step)}
                    {course.tasks.length > 1 ? 's' : ''}
                  </li>
                  {ownerName && <li>{ownerName}</li>}
                </InlineList>
                <TrainingOverview description={course.description} maxLines={3} />
              </TrainingInformation>
            </div>
          </CourseInfoContainer>
        </CourseRowItem>

        <SmallToggleRoundedIconButton
          aria-label={showSteps ? 'Collapse Course' : 'Expand Course'}
          onClick={toggleShowSteps}
          status={showSteps}
          style={{ marginLeft: Grid._4 }}
        >
          <i className="fas fa-chevron-down"></i>
        </SmallToggleRoundedIconButton>
      </CourseRow>
      <CollapsibleContent isOpen={showSteps}>
        <CollapsibleInner>
          <div>
            <TrackContainer>{index + 1 < length && <TrackLine />}</TrackContainer>
          </div>
          <div>
            <CourseSteps
              course={course}
              trainingId={trainingId}
              isWithinPathway
              nextAction={nextAction}
              userAccess={userAccess}
            />
          </div>
        </CollapsibleInner>
      </CollapsibleContent>
    </CourseContainer>
  );
};

export default PathwayCourse;
